import { useState, useEffect } from 'react';
import { Menu, Bell, X, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

// Simple relative time formatter
const formatRelativeTime = (date) => {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
};

export const Header = ({ onMenuClick, title }) => {
  const { profile, tier, isTrialActive, daysRemaining } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);

  // Load notifications and announcements
  useEffect(() => {
    if (!profile?.id) return;

    const loadNotifications = async () => {
      try {
        // Fetch user notifications
        const { data: notifs } = await supabase
          .from('user_notifications')
          .select('*')
          .eq('user_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(20);

        const notificationList = notifs || [];

        // Add trial reminder notification if applicable
        if (tier === 'trial' && isTrialActive && daysRemaining <= 7) {
          const trialNotif = {
            id: 'trial-reminder',
            type: 'trial_reminder',
            title: 'Trial Ending Soon',
            message: daysRemaining === 1 
              ? 'Your trial ends tomorrow! Subscribe to keep access.'
              : `Your trial ends in ${daysRemaining} days. Subscribe to continue using all features.`,
            created_at: new Date().toISOString(),
            read: false,
            persistent: true,
          };
          notificationList.unshift(trialNotif);
        }

        // Add plan expiry notification for non-trial users
        if (tier !== 'trial' && profile?.subscription_ends_at) {
          const expiryDate = new Date(profile.subscription_ends_at);
          const daysUntilExpiry = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
          
          if (daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
            const expiryNotif = {
              id: 'subscription-expiry',
              type: 'subscription_expiry',
              title: 'Subscription Expiring',
              message: daysUntilExpiry === 1
                ? 'Your subscription expires tomorrow!'
                : `Your subscription expires in ${daysUntilExpiry} days.`,
              created_at: new Date().toISOString(),
              read: false,
              persistent: true,
            };
            notificationList.unshift(expiryNotif);
          }
        }

        // Fetch unread HapiGuard anomaly alerts
        try {
          const { data: hgSite } = await supabase
            .from('hapiguard_sites')
            .select('id, domain')
            .eq('user_id', profile.id)
            .limit(1)
            .maybeSingle();

          if (hgSite) {
            const { data: hgAlerts } = await supabase
              .from('hapiguard_anomaly_alerts')
              .select('id, alert_type, severity, details, created_at')
              .eq('site_id', hgSite.id)
              .eq('acknowledged', false)
              .order('created_at', { ascending: false })
              .limit(5);

            for (const alert of (hgAlerts || [])) {
              const clickCount = alert.details?.click_count;
              notificationList.push({
                id:          `hg_${alert.id}`,
                type:        'hapiguard_alert',
                title:       `HapiGuard: ${(alert.alert_type || 'alert').replace(/_/g, ' ')} · ${alert.severity}`,
                message:     clickCount
                  ? `${clickCount} suspicious click${clickCount > 1 ? 's' : ''} detected on ${hgSite.domain}`
                  : `Anomaly detected on ${hgSite.domain}`,
                created_at:  alert.created_at,
                read:        false,
                link:        '/hapiguard',
                hg_alert_id: alert.id,
              });
            }

            // Sort all notifications newest-first
            notificationList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          }
        } catch { /* HapiGuard tables may not exist yet */ }

        setNotifications(notificationList);
        setUnreadCount(notificationList.filter(n => !n.read).length);

        // Check for active announcements (per-session modal)
        const sessionKey = `announcement_dismissed_${profile.id}`;
        const dismissedAnnouncements = JSON.parse(sessionStorage.getItem(sessionKey) || '[]');

        const { data: announcements } = await supabase
          .from('admin_announcements')
          .select('*')
          .eq('is_active', true)
          .eq('show_modal', true)
          .or("delivery.eq.revhapi_app,delivery.eq.both,delivery.is.null")
          .order('created_at', { ascending: false })
          .limit(1);

        if (announcements && announcements.length > 0) {
          const announcement = announcements[0];
          if (!dismissedAnnouncements.includes(announcement.id)) {
            setCurrentAnnouncement(announcement);
            setShowAnnouncementModal(true);
          }
        }
      } catch (error) {
        // Tables might not exist yet, silently ignore
        console.log('Notifications not available yet');
      }
    };

    loadNotifications();

    // Subscribe to real-time notifications
    const channel = supabase
      .channel('notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_notifications',
        filter: `user_id=eq.${profile.id}`,
      }, (payload) => {
        setNotifications(prev => [payload.new, ...prev]);
        setUnreadCount(prev => prev + 1);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, tier, isTrialActive, daysRemaining]);

  const markAsRead = async (notifId) => {
    if (notifId === 'trial-reminder' || notifId === 'subscription-expiry') return;
    try {
      if (notifId.startsWith('hg_')) {
        // HapiGuard alert — acknowledge in hapiguard_anomaly_alerts
        const notif = notifications.find(n => n.id === notifId);
        if (notif?.hg_alert_id) {
          await supabase
            .from('hapiguard_anomaly_alerts')
            .update({ acknowledged: true })
            .eq('id', notif.hg_alert_id);
        }
      } else {
        await supabase
          .from('user_notifications')
          .update({ read: true })
          .eq('id', notifId);
      }
      setNotifications(prev => prev.map(n =>
        n.id === notifId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const regularIds = notifications
        .filter(n => !n.read && !n.id.startsWith?.('hg_') && n.id !== 'trial-reminder' && n.id !== 'subscription-expiry')
        .map(n => n.id);
      const hgAlertIds = notifications
        .filter(n => !n.read && n.id.startsWith?.('hg_') && n.hg_alert_id)
        .map(n => n.hg_alert_id);

      await Promise.all([
        regularIds.length > 0
          ? supabase.from('user_notifications').update({ read: true }).in('id', regularIds)
          : Promise.resolve(),
        hgAlertIds.length > 0
          ? supabase.from('hapiguard_anomaly_alerts').update({ acknowledged: true }).in('id', hgAlertIds)
          : Promise.resolve(),
      ]);

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const dismissAnnouncement = () => {
    if (currentAnnouncement) {
      const sessionKey = `announcement_dismissed_${profile.id}`;
      const dismissed = JSON.parse(sessionStorage.getItem(sessionKey) || '[]');
      dismissed.push(currentAnnouncement.id);
      sessionStorage.setItem(sessionKey, JSON.stringify(dismissed));
    }
    setShowAnnouncementModal(false);
    setCurrentAnnouncement(null);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'trial_reminder':
      case 'subscription_expiry': return '⏰';
      case 'admin_message':       return '📢';
      case 'support_reply':       return '💬';
      case 'hapiguard_alert':     return '🛡️';
      default:                    return '🔔';
    }
  };

  return (
    <>
      <header className="sticky top-0 z-30 flex-shrink-0 flex h-16 items-center gap-4 border-b border-border bg-card px-4 sm:px-6">
        {/* Mobile menu button */}
        <button
          className="p-2 rounded-md hover:bg-accent lg:hidden"
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Page title */}
        <h1 className="text-lg font-semibold">{title}</h1>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Notification Bell */}
        <div className="relative">
          <button
            className="p-2 rounded-md hover:bg-accent relative"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowNotifications(false)} 
              />
              <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <h3 className="font-semibold">Notifications</h3>
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="text-xs text-primary hover:underline"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-muted-foreground">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No notifications</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div 
                        key={notif.id}
                        className={`px-4 py-3 border-b border-border last:border-0 hover:bg-accent/50 cursor-pointer transition-colors ${
                          !notif.read ? 'bg-primary/5' : ''
                        }`}
                        onClick={() => {
                          markAsRead(notif.id);
                          if (notif.link) { setShowNotifications(false); window.location.href = notif.link; }
                        }}
                      >
                        <div className="flex gap-3">
                          <span className="text-lg">{getNotificationIcon(notif.type)}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">{notif.title}</p>
                              {!notif.read && (
                                <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                              {notif.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatRelativeTime(notif.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Announcement Modal */}
      {showAnnouncementModal && currentAnnouncement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={dismissAnnouncement}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold">{currentAnnouncement.title}</h2>
              <button 
                onClick={dismissAnnouncement}
                className="p-1 rounded-md hover:bg-accent"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <div 
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: currentAnnouncement.content }}
              />
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-border">
              <button 
                onClick={dismissAnnouncement}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
